import { Button, DatePicker, Drawer, Select, Space, Tooltip } from "antd";
import { ColumnsType } from "antd/es/table";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAppSelector } from "../../../app/store";
import DashCollectionIcon from "../../../assets/icons/DashCollectionIcon";
import DashDepositIcon from "../../../assets/icons/DashDepositIcon";
import DashFieldOfficerIcon from "../../../assets/icons/DashFieldOfficerIcon";
import DashRemainingIcon from "../../../assets/icons/DashRemainingIcon";
import FilterIconThree from "../../../assets/icons/FilterIconThree";
import SelectDownIcon from "../../../assets/icons/SelectDownIcon";
import ViewIcon from "../../../assets/icons/ViewIcon";
import CustomTable from "../../../components/ui/CustomTable";
import IconButton from "../../../components/ui/IconButton";
import Loader from "../../../components/ui/Loader";
import {
  useGetAllCollectionPointQuery,
  useGetCashCollectionState1Query,
  useGetCashCollectionState2Query,
  useGetCashCollectionState3Query,
  useGetCashCollectionState4Query,
  useGetTransactionsQuery,
} from "../../../features/cashCollection/cashCollectionSetup/cashCollectionSetupApi";
import {
  useGetAllAreaQuery,
  useGetAllRouteQuery,
  useGetAllTerritoryQuery,
  useGetAllZoneQuery,
} from "../../../features/cashCollection/geoSetup/geoSetupApi";
import { useGetUserListQuery } from "../../../features/portal/Products/productsApi";
import useError from "../../../hooks/useError";
import useModal from "../../../hooks/useModal";
import useTable from "../../../hooks/useTable";
import SidebarLayout from "../../../layouts/SidebarLayout";
import { formatDate, formatTime } from "../../../lib/time";
import {
  GetAllTransactionsDataParamsType,
  GetAllTransactionsDataType,
} from "../../../types/cashCollection/cashCollectionSetup";
import { countParPageSerialNumber } from "../../../utils/number";
import { capitalize } from "../../../utils/string";
import { addKeyInArray, formatNumber } from "../../../utils/utils";
import CashCollectionDetailsModal from "../FieldOfficers/CashCollectionDetailsModal";
import CollectionPointCart from "./CollectionPointCart";
import DepositTrackingCart from "./DepositTrackingCart";

export default function Dashboard() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const { RangePicker } = DatePicker;
  const [search] = useSearchParams();
  const projectId = localStorage.getItem("product_id");

  const {
    page,
    limit,
    handlePagination,
    handleLimit,
    filter,
    handleFilter,
    handleSearch,
    keyword,
  } = useTable<GetAllTransactionsDataParamsType>();

  const [selectedZoneId, setSelectedZoneId] = useState<number | null>(
    filter?.zoneId ?? null
  );
  const [selectedAreaId, setSelectedAreaId] = useState<number | null>(
    filter?.areaId ?? null
  );
  const [selectedTerritoryId, setSelectedTerritoryId] = useState<number | null>(
    filter?.territoryId ?? null
  );
  const [selectedRouteId, setSelectedRouteId] = useState<number | null>(
    filter?.routeId ?? null
  );
  const [selectedUserId, setSelectedUserId] = useState<number | null>(
    filter?.createdBy ?? null
  );

  const [selectedFilterZoneId, setSelectedFilterZoneId] = useState<
    number | null
  >(null);
  const [selectedFilterAreaId, setSelectedFilterAreaId] = useState<
    number | null
  >(null);
  const [selectedFilterTerritoryId, setSelectedFilterTerritoryId] = useState<
    number | null
  >(null);
  const [selectedFilterRouteId, setSelectedFilterRouteId] = useState<
    number | null
  >(null);
  const [selectedFilterUserId, setSelectedFilterUserId] = useState<
    number | null
  >(null);

  const [dateRange, setDateRange] = useState<
    [string | undefined, string | undefined]
  >([undefined, undefined]);

  const [dateRangeChart, setDateRangeChart] = useState<
    [string | undefined, string | undefined]
  >([undefined, undefined]);

  const [dateRangePieChart, setDateRangePieChart] = useState<
    [string | undefined, string | undefined]
  >([undefined, undefined]);

  const {
    open: openViewModal,
    handleOpen: handleViewOpen,
    handleClose: handleViewClose,
  } = useModal<any, "VIEW">();

  const { user } = useAppSelector((state) => state.auth);

  const { isError, error, isLoading, data, isFetching } =
    useGetTransactionsQuery({
      params: {
        ...filter,
        skip: undefined,
        page,
        limit,
        companyId: user?.data?.company_id,
      },
    });

  const {
    isError: getAllCollectionPointIsError,
    error: getAllCollectionPointError,
    isLoading: getAllCollectionPointIsLoading,
    data: getAllCollectionPointData,
  } = useGetAllCollectionPointQuery({
    params: {
      page: 1,
      limit: 100000000,
    },
  });

  // Get Zone
  const {
    isError: getAllZoneIsError,
    error: getAllZoneError,
    data: getAllZoneData,
    isLoading: getAllZoneIsLoading,
  } = useGetAllZoneQuery({
    params: {
      page: 1,
      limit: 100000000,
    },
  });

  // Get all Areas
  const {
    isError: getAllAreaIsError,
    error: getAllAreaError,
    data: getAllAreaData,
    isLoading: getAllAreaIsLoading,
  } = useGetAllAreaQuery(
    {
      params: {
        page: 1,
        limit: 100000000,
        zoneId: selectedZoneId ?? selectedFilterZoneId ?? undefined,
      },
    },
    {
      skip: !(selectedZoneId || selectedFilterZoneId),
    }
  );

  // Get all Territories
  const {
    isError: getAllTerritoryIsError,
    error: getAllTerritoryError,
    data: getAllTerritoryData,
    isLoading: getAllTerritoryIsLoading,
  } = useGetAllTerritoryQuery(
    {
      params: {
        page: 1,
        limit: 100000000,
        areaId: selectedAreaId ?? selectedFilterAreaId ?? undefined,
      },
    },
    {
      skip: !(selectedAreaId || selectedFilterAreaId),
    }
  );

  // Get all Routes
  const {
    isError: getAllRouteIsError,
    error: getAllRouteError,
    data: getAllRouteData,
    isLoading: getAllRouteIsLoading,
  } = useGetAllRouteQuery(
    {
      params: {
        page: 1,
        limit: 100000000,
        territoryId:
          selectedTerritoryId ?? selectedFilterTerritoryId ?? undefined,
      },
    },
    {
      skip: !(selectedTerritoryId || selectedFilterTerritoryId),
    }
  );

  const {
    isError: getCashCollectionState1IsError,
    error: getCashCollectionState1Error,
    isLoading: getCashCollectionState1IsLoading,
    data: getCashCollectionState1Data,
  } = useGetCashCollectionState1Query({
    params: {
      createdBy: selectedFilterUserId ? selectedFilterUserId : undefined,
      zoneId: selectedFilterZoneId ? selectedFilterZoneId : undefined,
      areaId: selectedFilterAreaId ? selectedFilterAreaId : undefined,
      territoryId: selectedFilterTerritoryId
        ? selectedFilterTerritoryId
        : undefined,
      routeId: selectedFilterRouteId ? selectedFilterRouteId : undefined,
      dateFrom: dateRange[0],
      dateTo: dateRange[1],
    },
  });

  const {
    isError: getCashCollectionState2IsError,
    error: getCashCollectionState2Error,
    isLoading: getCashCollectionState2IsLoading,
    data: getCashCollectionState2Data,
  } = useGetCashCollectionState2Query({
    params: {
      createdBy: selectedFilterUserId ? selectedFilterUserId : undefined,
      zoneId: selectedFilterZoneId ? selectedFilterZoneId : undefined,
      areaId: selectedFilterAreaId ? selectedFilterAreaId : undefined,
      territoryId: selectedFilterTerritoryId
        ? selectedFilterTerritoryId
        : undefined,
      routeId: selectedFilterRouteId ? selectedFilterRouteId : undefined,
      dateFrom: dateRange[0],
      dateTo: dateRange[1],
    },
  });

  const {
    isError: getCashCollectionState3IsError,
    error: getCashCollectionState3Error,
    isLoading: getCashCollectionState3IsLoading,
    data: getCashCollectionState3Data,
  } = useGetCashCollectionState3Query({
    params: {
      createdBy: selectedFilterUserId ? selectedFilterUserId : undefined,
      zoneId: selectedFilterZoneId ? selectedFilterZoneId : undefined,
      areaId: selectedFilterAreaId ? selectedFilterAreaId : undefined,
      territoryId: selectedFilterTerritoryId
        ? selectedFilterTerritoryId
        : undefined,
      routeId: selectedFilterRouteId ? selectedFilterRouteId : undefined,
      dateFrom: dateRange[0] ?? dateRangeChart[0] ?? undefined,
      dateTo: dateRange[1] ?? dateRangeChart[1] ?? undefined,
    },
  });

  const {
    isError: getCashCollectionState4IsError,
    error: getCashCollectionState4Error,
    isLoading: getCashCollectionState4IsLoading,
    data: getCashCollectionState4Data,
  } = useGetCashCollectionState4Query({
    params: {
      createdBy: selectedFilterUserId ? selectedFilterUserId : undefined,
      zoneId: selectedFilterZoneId ? selectedFilterZoneId : undefined,
      areaId: selectedFilterAreaId ? selectedFilterAreaId : undefined,
      territoryId: selectedFilterTerritoryId
        ? selectedFilterTerritoryId
        : undefined,
      routeId: selectedFilterRouteId ? selectedFilterRouteId : undefined,
      dateFrom: dateRange[0] ?? dateRangePieChart[0] ?? undefined,
      dateTo: dateRange[1] ?? dateRangePieChart[1] ?? undefined,
    },
  });

  const {
    isError: getAllUserIsError,
    error: getAllUserError,
    isLoading: getAllUserIsLoading,
    data: getAllUserData,
  } = useGetUserListQuery(
    {
      id: projectId,
      page: 1,
      limit: 1000000000000,
      companyId: user?.data?.company_id,
      roleId: undefined,
    },
    {
      skip: !user?.data?.company_id || !projectId,
    }
  );

  useError(isError, error, "Get transaction list fail");
  useError(
    getAllCollectionPointIsError,
    getAllCollectionPointError,
    "Get collection point data fail"
  );
  useError(getAllAreaIsError, getAllAreaError, "Get all area fail");
  useError(getAllZoneIsError, getAllZoneError, "Get all zone fail");
  useError(
    getAllTerritoryIsError,
    getAllTerritoryError,
    "Get all territory fail"
  );
  useError(getAllRouteIsError, getAllRouteError, "Get all route fail");
  useError(
    getCashCollectionState1IsError,
    getCashCollectionState1Error,
    "Get dashboard data fail"
  );
  useError(
    getCashCollectionState2IsError,
    getCashCollectionState2Error,
    "Get dashboard data fail"
  );
  useError(
    getCashCollectionState3IsError,
    getCashCollectionState3Error,
    "Get dashboard data fail"
  );
  useError(
    getCashCollectionState4IsError,
    getCashCollectionState4Error,
    "Get dashboard data fail"
  );
  useError(getAllUserIsError, getAllUserError, "Get all users fail");

  const showDrawer = () => {
    setOpen(true);
  };

  const onClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    const type = search.get("type");
    const dynamicId = search.get("dynamic-id");
    const dynamicKeyword = search.get("dynamic-keyword");

    if (type) {
      localStorage.setItem("product-type", JSON.stringify(type));
    }

    if (dynamicId && dynamicKeyword) {
      localStorage.setItem(
        "dynamic",
        JSON.stringify({
          dynamicId,
          dynamicKeyword,
        })
      );
    }

    const t = setTimeout(() => {
      setLoading(false);
    }, 500);

    return () => clearTimeout(t);
  }, [search]);

  const handleDateRangeChange = (value: string) => {
    const today = new Date();
    let dateFrom: string;
    let dateTo: string;

    // helper to add one day
    const addOneDay = (date: Date) => {
      const newDate = new Date(date);
      newDate.setDate(date.getDate() + 1);
      return newDate.toISOString().split("T")[0];
    };

    switch (value) {
      case "7": // This Week
        const weekAgo = new Date(today);
        weekAgo.setDate(today.getDate() - 6); // past 7 days including today
        dateFrom = weekAgo.toISOString().split("T")[0];
        dateTo = addOneDay(today);
        break;

      case "30": // This Month
        const monthAgo = new Date(today);
        monthAgo.setDate(today.getDate() - 29);
        dateFrom = monthAgo.toISOString().split("T")[0];
        dateTo = addOneDay(today);
        break;

      case "365": // This Year
        const startOfYear = new Date(today.getFullYear(), 0, 1);
        dateFrom = startOfYear.toISOString().split("T")[0];
        dateTo = addOneDay(today);
        break;

      case "2024": // Last Year
        const lastYear = today.getFullYear() - 1;
        const startOfLastYear = new Date(lastYear, 0, 1);
        const endOfLastYear = new Date(lastYear, 11, 31);
        dateFrom = startOfLastYear.toISOString().split("T")[0];
        dateTo = addOneDay(endOfLastYear);
        break;

      default:
        dateFrom = "";
        dateTo = "";
    }

    setDateRangeChart([dateFrom, dateTo]);
  };

  const handlePieChartDateRangeChange = (value: string) => {
    const today = new Date();
    const todayStr = today.toISOString().split("T")[0];
    let dateFrom: string;
    let dateTo: string = todayStr; // default to today

    // helper to add one day
    const addOneDay = (date: Date) => {
      const newDate = new Date(date);
      newDate.setDate(date.getDate() + 1);
      return newDate.toISOString().split("T")[0];
    };

    switch (value) {
      case "1": // Today
        dateFrom = todayStr;
        dateTo = todayStr;
        break;

      case "7": // This Week
        const weekAgo = new Date(today);
        weekAgo.setDate(today.getDate() - 6); // past 7 days including today
        dateFrom = weekAgo.toISOString().split("T")[0];
        dateTo = addOneDay(today);
        break;

      case "30": // This Month
        const monthAgo = new Date(today);
        monthAgo.setDate(today.getDate() - 29);
        dateFrom = monthAgo.toISOString().split("T")[0];
        dateTo = addOneDay(today);
        break;

      case "365": // This Year
        const startOfYear = new Date(today.getFullYear(), 0, 1);
        dateFrom = startOfYear.toISOString().split("T")[0];
        dateTo = addOneDay(today);
        break;

      case "2024": // Last Year
        const lastYear = today.getFullYear() - 1;
        const startOfLastYear = new Date(lastYear, 0, 1);
        const endOfLastYear = new Date(lastYear, 11, 31);
        dateFrom = startOfLastYear.toISOString().split("T")[0];
        dateTo = addOneDay(endOfLastYear);
        break;

      default:
        dateFrom = "";
        dateTo = "";
    }

    setDateRangePieChart([dateFrom, dateTo]);
  };

  useEffect(() => {
    if (selectedFilterZoneId === null) {
      setSelectedFilterAreaId(null);
    }
  }, [selectedFilterZoneId]);

  const viewData = openViewModal?.data;

  const detailsData = [
    {
      label: "Date & Time",
      value: viewData?.createdAt
        ? `${formatDate(viewData.createdAt)} ${formatTime(viewData.createdAt)}`
        : "N/A",
    },
    {
      label: "Collection Point",
      value: viewData?.collectionPoint?.organizationName
        ? capitalize(viewData?.collectionPoint?.organizationName)
        : "N/A",
    },
    {
      label: "Zone",
      value: viewData?.zoneName ? capitalize(viewData?.zoneName) : "N/A",
    },
    {
      label: "Area",
      value: viewData?.areaName ? capitalize(viewData?.areaName) : "N/A",
    },
    {
      label: "Territory",
      value: viewData?.territoryName
        ? capitalize(viewData?.territoryName)
        : "N/A",
    },
    {
      label: "Route",
      value: viewData?.routeName ? capitalize(viewData?.routeName) : "N/A",
    },
    {
      label: "Field Officer",
      value: viewData?.username ? capitalize(viewData?.username) : "N/A",
    },
    {
      label: "Collection (BDT)",
      value: viewData?.amount ? `৳ ${formatNumber(viewData?.amount)}` : "৳ 0",
    },
    // {
    //   label: "Deposit (BDT)",
    //   value: viewData?.receiveAmmount
    //     ? `৳ ${formatNumber(viewData?.receiveAmmount)}`
    //     : "৳ 0",
    // },
  ];

  const total =
    (getCashCollectionState1Data?.data?.totalCollection || 0) -
    (getCashCollectionState1Data?.data?.totalDeposit || 0);
  const dashboardCards = [
    {
      id: 1,
      icon: <DashCollectionIcon />,
      title: "Total Collection",
      value: getCashCollectionState1Data?.data?.totalCollection
        ? `৳ ${formatNumber(
            getCashCollectionState1Data?.data?.totalCollection
          )} `
        : "৳ 0",
    },
    {
      id: 2,
      icon: <DashDepositIcon />,
      title: "Total Deposit",
      value: getCashCollectionState1Data?.data?.totalDeposit
        ? `৳ ${formatNumber(getCashCollectionState1Data?.data?.totalDeposit)}`
        : "৳ 0",
    },
    {
      id: 3,
      icon: <DashRemainingIcon />,
      title: "Remaining",
      value: total ? `৳ ${formatNumber(total)}` : "৳ 0",
    },
    {
      id: 4,
      icon: <DashFieldOfficerIcon />,
      title: "Active Field Officers",
      value: getCashCollectionState2Data?.data?.count
        ? getCashCollectionState2Data?.data?.count
        : "0",
    },
  ];

  const columns: ColumnsType<GetAllTransactionsDataType> = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: <div className="whitespace-nowrap">Date & Time</div>,
      dataIndex: "createdAt",
      align: "center" as const,
      render: (val: string | null | undefined) =>
        val ? `${formatDate(val)} ${formatTime(val)}` : "N/A",
    },
    {
      title: <div className="text-nowrap">Collection Point</div>,
      dataIndex: "collectionPoint",
      align: "center",
      render: (val) => {
        return val?.organizationName
          ? capitalize(val?.organizationName)
          : "N/A";
      },
    },
    {
      title: <div className="text-nowrap">Route</div>,
      dataIndex: "routeName",
      align: "center",
      render: (val) => {
        return val ? capitalize(val) : "N/A";
      },
    },
    {
      title: <div className="text-nowrap">Field Officer</div>,
      dataIndex: "username",
      align: "center",
      render: (val) => {
        return val ? capitalize(val) : "N/A";
      },
    },
    {
      title: <div className="text-nowrap">Collection (BDT)</div>,
      dataIndex: "amount",
      align: "center",
      render: (val) => {
        return val ? formatNumber(val) : "0";
      },
    },

    {
      title: "Actions",
      dataIndex: "id",
      align: "center",
      render: (val, record: any) => (
        <div className="flex justify-center items-center gap-2">
          <Button
            className="action-button disabled:!bg-[#ddd] !border-none"
            size="small"
            icon={<ViewIcon />}
            onClick={() => handleViewOpen({ type: "VIEW", data: record })}
          />
        </div>
      ),
    },
  ];

  return (
    <>
      <SidebarLayout title="Dashboard">
        {loading ||
        getCashCollectionState1IsLoading ||
        getCashCollectionState2IsLoading ||
        getCashCollectionState3IsLoading ||
        getCashCollectionState4IsLoading ? (
          <Loader />
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
              <div className="flex flex-wrap items-center gap-3">
                <Select
                  placeholder="Select Field Officer"
                  suffixIcon={<SelectDownIcon />}
                  showSearch
                  allowClear
                  className="!min-w-[110px]"
                  options={
                    getAllUserData?.data?.users?.map((user: any) => ({
                      label: capitalize(user.user.username),
                      value: user.user.id,
                    })) || []
                  }
                  value={selectedFilterUserId}
                  onChange={(value) => setSelectedFilterUserId(value ?? null)}
                  optionFilterProp="label"
                />
                <Select
                  placeholder="Zone"
                  suffixIcon={<SelectDownIcon />}
                  showSearch
                  allowClear
                  className="!min-w-[110px]"
                  options={
                    getAllZoneData?.data?.rows?.map((val: any) => ({
                      label: capitalize(val?.zoneName),
                      value: val?.id,
                    })) || []
                  }
                  value={selectedFilterZoneId}
                  onChange={(value) => {
                    setSelectedFilterZoneId(value ?? null);
                    if (!value) {
                      // Zone cleared → reset all children
                      setSelectedFilterAreaId(null);
                      setSelectedFilterTerritoryId(null);
                      setSelectedFilterRouteId(null);
                    }
                  }}
                  optionFilterProp="label"
                />

                <Select
                  placeholder="Area"
                  suffixIcon={<SelectDownIcon />}
                  showSearch
                  allowClear
                  className="!min-w-[110px]"
                  disabled={!selectedFilterZoneId}
                  options={
                    getAllAreaData?.data?.rows?.map((val: any) => ({
                      label: capitalize(val?.areaName),
                      value: val?.id,
                    })) || []
                  }
                  value={selectedFilterAreaId}
                  onChange={(value) => {
                    setSelectedFilterAreaId(value ?? null);
                    if (!value) {
                      // Area cleared → reset children
                      setSelectedFilterTerritoryId(null);
                      setSelectedFilterRouteId(null);
                    }
                  }}
                  optionFilterProp="label"
                />

                <Select
                  placeholder="Territory"
                  suffixIcon={<SelectDownIcon />}
                  showSearch
                  allowClear
                  className="!min-w-[110px]"
                  disabled={!selectedFilterAreaId}
                  options={
                    getAllTerritoryData?.data?.rows?.map((val: any) => ({
                      label: capitalize(val?.territoryName),
                      value: val?.id,
                    })) || []
                  }
                  value={selectedFilterTerritoryId}
                  onChange={(value) => {
                    setSelectedFilterTerritoryId(value ?? null);
                    if (!value) {
                      // Territory cleared → reset Route
                      setSelectedFilterRouteId(null);
                    }
                  }}
                  optionFilterProp="label"
                />

                <Select
                  placeholder="Route"
                  suffixIcon={<SelectDownIcon />}
                  showSearch
                  allowClear
                  className="!min-w-[110px]"
                  disabled={!selectedFilterTerritoryId}
                  options={
                    getAllRouteData?.data?.rows?.map((val: any) => ({
                      label: capitalize(val?.routeName),
                      value: val?.id,
                    })) || []
                  }
                  value={selectedFilterRouteId}
                  onChange={(value) => setSelectedFilterRouteId(value ?? null)}
                  optionFilterProp="label"
                />
              </div>

              <div>
                <RangePicker
                  allowClear
                  format="YYYY-MM-DD"
                  placeholder={["Start Date", "End Date"]}
                  onChange={(dates, dateStrings) => {
                    if (dates?.[0] && dates?.[1]) {
                      setDateRange([
                        dateStrings[0], // start date
                        dates[1].add(1, "day").format("YYYY-MM-DD"), // ✅ safely add +1 day
                      ]);
                    } else {
                      setDateRange([undefined, undefined]);
                    }
                  }}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
              {dashboardCards.map((card) => (
                <div
                  key={card.id}
                  className="p-4 bg-white rounded-lg outline outline-1 outline-offset-[-1px] outline-zinc-200 flex flex-col justify-start items-start gap-2"
                >
                  <div className="self-stretch inline-flex justify-start items-center gap-2.5">
                    <div>{card.icon}</div>
                    <div className="flex-1 justify-start text-slate-600 text-base font-normal">
                      {card.title}
                    </div>
                  </div>

                  <Tooltip title={card.value}>
                    <div className="text-zinc-700 text-3xl font-normal truncate max-w-full">
                      {card.value}
                    </div>
                  </Tooltip>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-4 mb-5">
              <div className="basis-[55%] max-w-[55%]">
                <div
                  className="lg:col-span-2 p-4 rounded-md dark:bg-primaryBackground bg:white !dark:border-[#cccccc] 
      !border-[#cccccc] border border-solid h-[460px]"
                >
                  <div className="flex justify-between items-center">
                    <div className="justify-start text-zinc-700 text-xl font-semibold">
                      Collection and Deposit Tracking
                    </div>

                    <Select
                      placeholder="Select"
                      showSearch
                      options={[
                        { label: "This Week", value: "7" },
                        { label: "This Month", value: "30" },
                        { label: "This Year", value: "365" },
                        { label: "Last Year", value: "2024" },
                      ]}
                      className="!min-w-[110px]"
                      suffixIcon={<SelectDownIcon />}
                      allowClear
                      optionFilterProp="label"
                      onChange={handleDateRangeChange}
                    />
                  </div>

                  <DepositTrackingCart
                    getCashCollectionState3Data={getCashCollectionState3Data}
                  />
                </div>
              </div>
              <div className="basis-[45%] max-w-[45%]">
                <div
                  className="lg:col-span-2 p-4 rounded-md dark:bg-primaryBackground bg:white !dark:border-[#cccccc]
                         !border-[#cccccc] border border-solid h-[460px]"
                >
                  <div className="flex justify-between items-center">
                    <div className="justify-start text-zinc-700 text-xl font-semibold">
                      Collection Point Coverage
                    </div>

                    <Select
                      placeholder="Select"
                      showSearch
                      options={[
                        { label: "Today", value: "1" },
                        { label: "This Week", value: "7" },
                        { label: "This Month", value: "30" },
                        { label: "This Year", value: "365" },
                        { label: "Last Year", value: "2024" },
                      ]}
                      onChange={handlePieChartDateRangeChange}
                      className="!min-w-[110px]"
                      suffixIcon={<SelectDownIcon />}
                      allowClear
                      optionFilterProp="label"
                    />
                  </div>

                  <div className="mt-5 flex justify-center">
                    <CollectionPointCart
                      getCashCollectionState4Data={getCashCollectionState4Data}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="justify-start text-zinc-700 text-xl font-semibold mb-5">
              Recent Transactions
            </div>

            <CustomTable
              loading={isLoading || isFetching}
              limit={limit}
              page={page}
              columns={columns}
              total={data?.data?.count}
              dataSource={addKeyInArray(data?.data?.rows)}
              handleLimit={handleLimit}
              handlePagination={handlePagination}
              filterSection={
                <>
                  <Select
                    placeholder="Select Collection Point"
                    suffixIcon={<SelectDownIcon />}
                    showSearch
                    allowClear
                    style={{ minWidth: 210, width: "100%" }}
                    options={
                      getAllCollectionPointData?.data?.rows?.map(
                        (item: any) => ({
                          label: capitalize(item?.organizationName),
                          value: item?.id,
                        })
                      ) || []
                    }
                    value={filter?.collectionPointId ?? undefined}
                    onChange={(value) =>
                      handleFilter("collectionPointId", value)
                    }
                    optionFilterProp="label"
                  />

                  <RangePicker
                    allowClear
                    format="YYYY-MM-DD"
                    placeholder={["Start Date", "End Date"]}
                    onChange={(dates) => {
                      if (dates && dates[0] && dates[1]) {
                        handleFilter("dateFrom", dates[0].format("YYYY-MM-DD"));
                        handleFilter(
                          "dateTo",
                          dates[1].add(1, "day").format("YYYY-MM-DD")
                        );
                      } else {
                        // reset if cleared
                        handleFilter("dateFrom", undefined);
                        handleFilter("dateTo", undefined);
                      }
                    }}
                  />

                  <IconButton
                    className="flex items-center text-[#7a8799]"
                    icon={<FilterIconThree />}
                    onClick={() => showDrawer()}
                  >
                    Filter
                  </IconButton>
                </>
              }
            />
          </>
        )}
      </SidebarLayout>

      <Drawer open={open} width={400} onClose={onClose}>
        <div className="justify-start text-zinc-700 text-xl font-semibold">
          Filter
        </div>

        <div>
          {/* Zone */}
          <Select
            placeholder="Select Zone"
            suffixIcon={<SelectDownIcon />}
            showSearch
            allowClear
            className="w-full mt-8"
            options={
              getAllZoneData?.data?.rows?.map((val: any) => ({
                label: capitalize(val?.zoneName),
                value: val?.id,
              })) || []
            }
            value={selectedZoneId}
            onChange={(value) => {
              setSelectedZoneId(value ?? null);
              setSelectedAreaId(null);
              setSelectedTerritoryId(null);
              setSelectedRouteId(null);
            }}
            optionFilterProp="label"
          />

          {/* Area */}
          <Select
            placeholder="Select Area"
            suffixIcon={<SelectDownIcon />}
            showSearch
            allowClear
            className="w-full mt-5"
            disabled={!selectedZoneId}
            options={
              getAllAreaData?.data?.rows?.map((val: any) => ({
                label: capitalize(val?.areaName),
                value: val?.id,
              })) || []
            }
            value={selectedAreaId}
            onChange={(value) => {
              setSelectedAreaId(value ?? null);
              setSelectedTerritoryId(null);
              setSelectedRouteId(null);
            }}
            optionFilterProp="label"
          />

          {/* Territory */}
          <Select
            placeholder="Select Territory"
            suffixIcon={<SelectDownIcon />}
            showSearch
            allowClear
            className="w-full mt-5"
            disabled={!selectedAreaId}
            options={
              getAllTerritoryData?.data?.rows?.map((val: any) => ({
                label: capitalize(val?.territoryName),
                value: val?.id,
              })) || []
            }
            value={selectedTerritoryId}
            onChange={(value) => {
              setSelectedTerritoryId(value ?? null);
              setSelectedRouteId(null);
            }}
            optionFilterProp="label"
          />

          {/* Route */}
          <Select
            placeholder="Route"
            suffixIcon={<SelectDownIcon />}
            showSearch
            allowClear
            className="w-full mt-5"
            disabled={!selectedTerritoryId}
            options={
              getAllRouteData?.data?.rows?.map((val: any) => ({
                label: capitalize(val?.routeName),
                value: val?.id,
              })) || []
            }
            value={selectedRouteId}
            onChange={(value) => setSelectedRouteId(value ?? null)}
            optionFilterProp="label"
          />

          {/* Field Officer */}
          <Select
            placeholder="Field Officer"
            suffixIcon={<SelectDownIcon />}
            showSearch
            allowClear
            className="w-full mt-5"
            options={
              getAllUserData?.data?.users?.map((user: any) => ({
                label: capitalize(user.user.username),
                value: user.user.id,
              })) || []
            }
            value={selectedUserId}
            onChange={(value) => setSelectedUserId(value ?? null)}
          />
        </div>

        <Space className="w-full mt-8" size={12}>
          {/* Cancel → clear everything */}
          <Button
            className="normal-dark-button modal-button !h-[40px]"
            onClick={() => {
              setSelectedZoneId(null);
              setSelectedAreaId(null);
              setSelectedTerritoryId(null);
              setSelectedRouteId(null);
              handleFilter("zoneId", undefined);
              handleFilter("areaId", undefined);
              handleFilter("territoryId", undefined);
              handleFilter("routeId", undefined);
              handleFilter("createdBy", undefined);
              onClose();
            }}
            size="middle"
          >
            Cancel
          </Button>

          {/* Apply Filter */}
          <Button
            className="modal-button !h-[40px]"
            type="primary"
            size="middle"
            onClick={() => {
              handleFilter("zoneId", selectedZoneId ?? undefined);
              handleFilter("areaId", selectedAreaId ?? undefined);
              handleFilter("territoryId", selectedTerritoryId ?? undefined);
              handleFilter("routeId", selectedRouteId ?? undefined);
              handleFilter("createdBy", selectedUserId ?? undefined);
              onClose();
            }}
          >
            Filter
          </Button>
        </Space>
      </Drawer>

      {openViewModal?.type === "VIEW" && (
        <CashCollectionDetailsModal
          open={openViewModal?.open}
          handleClose={handleViewClose}
        >
          <div>
            <div className="justify-start text-zinc-700 text-xl font-medium">
              Details
            </div>

            <div className="pr-4 mt-5">
              {detailsData.map((item, index) => (
                <div
                  key={index}
                  className={`flex justify-between py-2 ${
                    index !== detailsData.length - 1
                      ? "border-b border-[#D7DFE9]"
                      : ""
                  }`}
                >
                  <span className="text-slate-500 text-base font-normal">
                    {item.label}
                  </span>
                  <span
                    className={`text-base font-normal px-2 py-1 rounded ${
                      item.value === "Active"
                        ? "bg-green-100 text-green-700"
                        : item.value === "Inactive"
                        ? "bg-red-100 text-red-700"
                        : "text-zinc-700"
                    }`}
                  >
                    {item.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </CashCollectionDetailsModal>
      )}
    </>
  );
}
